import React, { useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useIntl } from "react-intl"
import {
  Box,
  DownIcon,
  GlobeIcon,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useTheme
} from "@carrotfertility/carotene-core"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { useUserRole } from "#/components/partner-access/hooks/usePartnerAccess"
import { Locale } from "#/types/Locale"
import { getLocaleOptionsFromLocaleList, getSortedEnabledLocaleStrings } from "#/services/locale/localeUtils"
import { carrotClient } from "#/utils/CarrotClient"
import { useEnabledLocalesForCurrentUser } from "#/services/locale/hooks/useGetEnabledLocalesForCurrentUser"

interface LanguageSelectorProps {
  open: boolean
  anchorEl: null | HTMLElement
  localeOptions: { value: string; label: string }[]
  displayName: string
  currentLocale: string
  isDisabled: boolean
  ariaLabel: string
  onOpen: (event: React.MouseEvent<HTMLButtonElement>) => void
  onClose: () => void
  onSelectLocale: (localeString: string) => void
}

function LanguageSelector({
  open,
  anchorEl,
  localeOptions,
  displayName,
  currentLocale,
  isDisabled,
  ariaLabel,
  onOpen,
  onClose,
  onSelectLocale
}: LanguageSelectorProps): React.ReactElement {
  const theme = useTheme()

  return (
    <Box>
      <IconButton
        id="header-language-selector-button"
        disabled={isDisabled}
        aria-label={ariaLabel}
        aria-controls={open ? "header-language-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={onOpen}
        sx={(theme) => ({
          borderRadius: theme.tokens.borderRadius.sm,
          color: "unset",
          padding: theme.spacing(theme.tokens.spacing.md)
        })}
      >
        <Stack alignItems="center" direction="row" spacing={theme.tokens.spacing.xs}>
          <GlobeIcon fontSize="small" />
          <Typography
            component="span"
            sx={(theme) => ({
              display: "none",
              [theme.breakpoints.up("lg")]: {
                display: "inherit"
              }
            })}
          >
            {displayName}
          </Typography>
          <DownIcon
            sx={(theme) => ({
              display: "none",
              [theme.breakpoints.up("lg")]: {
                transition: "transform 100ms",
                transform: `rotate(${open ? "180deg" : "0deg"})`,
                display: "unset"
              }
            })}
          />
        </Stack>
      </IconButton>
      <Menu
        id="header-language-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
        MenuListProps={{
          "aria-labelledby": "header-language-selector-button"
        }}
      >
        {localeOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => onSelectLocale(option.value)}
            selected={currentLocale === option.value}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  )
}

function useLanguageSelector() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const queryClient = useQueryClient()
  const { refreshUserInfo } = useCurrentUser()
  const { isPartnerAccessingAccount } = useUserRole()
  const intl = useIntl()

  const { data: enabledLocalesForCurrentUser, isLoading } = useEnabledLocalesForCurrentUser()
  const sortedLocaleStrings = React.useMemo(
    () => (enabledLocalesForCurrentUser ? getSortedEnabledLocaleStrings(enabledLocalesForCurrentUser) : []),
    [enabledLocalesForCurrentUser]
  )
  const areMultipleLocalesEnabled = enabledLocalesForCurrentUser?.length > 1
  const localeOptions = React.useMemo(() => getLocaleOptionsFromLocaleList(sortedLocaleStrings), [sortedLocaleStrings])
  const displayName = Locale.getDisplayNameFromString(intl.locale)

  const { mutate: updateLocale } = useMutation({
    // eslint-disable-next-line no-restricted-syntax -- The user selects their preferredLocale in this dropdown
    mutationFn: (localeString: string) => carrotClient.updateUser({ preferredLocale: localeString }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["member"] })
      refreshUserInfo()
    },
    onError: () => {
      refreshUserInfo()
    }
  })

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleSelectLocale = (localeString: string) => {
    const locale = Locale.getLocaleFromString(localeString)
    if (!locale) {
      handleClose()
      return
    }
    handleClose()
    updateLocale(localeString)
  }

  return {
    open,
    anchorEl,
    isLoading,
    areMultipleLocalesEnabled,
    localeOptions,
    displayName,
    currentLocale: intl.locale,
    isDisabled: isPartnerAccessingAccount,
    ariaLabel: intl.formatMessage({ defaultMessage: "Preferred language" }),
    handleOpen,
    handleClose,
    handleSelectLocale
  }
}

export function HeaderLanguageSelector(): React.ReactElement | null {
  const {
    open,
    anchorEl,
    isLoading,
    areMultipleLocalesEnabled,
    localeOptions,
    displayName,
    currentLocale,
    isDisabled,
    ariaLabel,
    handleOpen,
    handleClose,
    handleSelectLocale
  } = useLanguageSelector()

  if (isLoading || !areMultipleLocalesEnabled) {
    return null
  }

  return (
    <LanguageSelector
      open={open}
      anchorEl={anchorEl}
      localeOptions={localeOptions}
      displayName={displayName}
      currentLocale={currentLocale}
      isDisabled={isDisabled}
      ariaLabel={ariaLabel}
      onOpen={handleOpen}
      onClose={handleClose}
      onSelectLocale={handleSelectLocale}
    />
  )
}
